/**
 * ELM327 OBD-II protocol parser — pure, no I/O.
 *
 * Reference:
 *   https://en.wikipedia.org/wiki/OBD-II_PIDs (Mode 01 PIDs)
 *   ELM327 spec: ASCII command + line-terminated response. Replies are
 *     of the form "41 0D 7F\r>" — mode+0x40, then PID, then data bytes.
 *
 * This parser handles only the PIDs we use for trip metrics:
 *   - 010C  Engine RPM       (2 bytes, divide by 4)
 *   - 010D  Vehicle Speed    (1 byte, km/h)
 *   - 0111  Throttle position (1 byte, percent * 255)
 *
 * Other return shapes worth knowing:
 *   - "NO DATA" — vehicle didn't respond
 *   - "?" — ELM327 didn't recognize the command
 *   - "SEARCHING..." — connection bring-up
 */

export type Pid = 'speed' | 'rpm' | 'throttle';

export interface PidQuery {
    pid: Pid;
    cmd: string;            // ASCII command to write to ELM327, e.g. "010D\r"
}

export const PID_QUERIES: Record<Pid, PidQuery> = {
    speed:    { pid: 'speed',    cmd: '010D\r' },
    rpm:      { pid: 'rpm',      cmd: '010C\r' },
    throttle: { pid: 'throttle', cmd: '0111\r' },
};

export interface PidReading {
    pid: Pid;
    /** parsed value in domain units: km/h for speed, RPM, % for throttle */
    value: number;
    /** ISO timestamp when the reading was taken (caller stamps) */
    at: number;
}

export type ParseResult =
    | { kind: 'reading'; reading: PidReading }
    | { kind: 'searching' }
    | { kind: 'no_data' }
    | { kind: 'unknown' }
    | { kind: 'unsupported_pid'; pid: string }
    | { kind: 'parse_error'; raw: string };

/**
 * Strip ELM327 noise: prompt char '>', whitespace, control chars.
 */
export function cleanLine(raw: string): string {
    return raw.replace(/>/g, '').replace(/\r|\n/g, ' ').trim();
}

/**
 * Parse a single ELM327 response line. Caller passes the cleaned line.
 * `nowSec` is parameterized so timestamping is reproducible in tests.
 *
 * Strategy: strip whitespace, left-pad to even byte count (handles 11-bit CAN IDs
 * like "7E8" which are 3 hex chars), then scan byte-by-byte for the first valid
 * mode-01 response token (0x41 followed by a PID byte with sufficient data bytes).
 * This handles CAN headers, command echo, compact formatting, and concatenated
 * frames without enumerating every possible shape.
 */
export function parseLine(raw: string, nowSec: number): ParseResult {
    const line = cleanLine(raw);
    if (line.length === 0) return { kind: 'parse_error', raw };
    if (/^SEARCHING/i.test(line)) return { kind: 'searching' };
    if (/^NO\s*DATA/i.test(line)) return { kind: 'no_data' };
    if (/^\?/.test(line)) return { kind: 'unknown' };

    const hex = line.replace(/\s+/g, '');
    if (!/^[0-9A-Fa-f]+$/.test(hex)) return { kind: 'parse_error', raw };

    // Left-pad odd-length hex to align bytes. CAN IDs are 11-bit (3 hex chars),
    // so stripping spaces from "7E8 03 41 0D 50" yields an odd-length string.
    const aligned = hex.length % 2 === 1 ? '0' + hex : hex;
    const bytes = (aligned.match(/.{2}/g) ?? []).map(b => parseInt(b, 16));

    // Reject frames whose first byte is a non-mode-01 response byte (0x42–0x4F).
    // Mode-N responses use 0x40+N, so 0x42=mode-02, 0x49=mode-09 (VIN), etc.
    // Valid mode-01 frames start with 0x41, a CAN header (e.g. 0x07 after padding),
    // a byte-count, or an echo byte (0x01). None of those are in 0x42–0x4F.
    if (bytes.length > 0 && bytes[0]! >= 0x42 && bytes[0]! <= 0x4F) {
        return { kind: 'parse_error', raw };
    }

    for (let i = 0; i < bytes.length - 1; i++) {
        if (bytes[i] !== 0x41) continue;

        const pidByte = bytes[i + 1]!;
        const data = bytes.slice(i + 2);

        switch (pidByte) {
            case 0x0D:
                if (data.length < 1) return { kind: 'parse_error', raw };
                return reading('speed', data[0]!, nowSec);
            case 0x0C:
                if (data.length < 2) return { kind: 'parse_error', raw };
                return reading('rpm', (data[0]! * 256 + data[1]!) / 4, nowSec);
            case 0x11:
                if (data.length < 1) return { kind: 'parse_error', raw };
                return reading('throttle', (data[0]! * 100) / 255, nowSec);
            default:
                return {
                    kind: 'unsupported_pid',
                    pid: pidByte.toString(16).toUpperCase().padStart(2, '0'),
                };
        }
    }

    return { kind: 'parse_error', raw };
}

/**
 * Init sequence to send right after connecting: reset, echo off, headers
 * off, auto protocol. Each command is sent and we wait for the prompt
 * before sending the next.
 */
export const INIT_COMMANDS: readonly string[] = Object.freeze([
    'ATZ\r',     // reset
    'ATE0\r',    // echo off
    'ATL0\r',    // linefeeds off
    'ATH0\r',    // headers off
    'ATSP0\r',   // auto-detect protocol
]);

function reading(pid: Pid, value: number, at: number): ParseResult {
    return { kind: 'reading', reading: { pid, value, at } };
}
